import { useUnderwritingSubmissionDetailPage } from '../underwriting.submission.detail.hooks';
import { CircularLoader, ErrorPageLayout } from '../../../presentation';
import { IRaterProps } from './types';
import { useGetRaterQuery, RaterMode, Submission } from '@converge/graphql';
import { memo } from 'react';
import { useUnderwritingSubmissionStateContext } from '../underwriting.submission.context';
import { RaterInnerProvider } from './rater.inner.provider';

export const Rater = memo(
  (props: IRaterProps) => {
    const { mode, isEditable } = props;

    const {
      params: { submissionUuid, quoteUuid },
    } = useUnderwritingSubmissionDetailPage();
    const {
      info: { allowCreatingQuotes, submission },
    } = useUnderwritingSubmissionStateContext();

    const {
      info: { underwritingQuote, userQuotes, policy },
      loading,
      error,
    } = useGetRaterQuery({
      submissionUuid,
      uuid: quoteUuid,
    });

    if (loading) {
      return <CircularLoader className="circular-loader-style" />;
    }

    if (error) {
      return (
        <ErrorPageLayout
          message={error.message}
          messageId="quote.errorLoading"
        />
      );
    }

    if (!underwritingQuote && mode !== RaterMode.CreateQuote) {
      return (
        <ErrorPageLayout
          message={`Rater quote not found: ${quoteUuid}`}
          messageId="quote.notFound"
        />
      );
    }

    if (!submission) {
      return (
        <ErrorPageLayout
          message="Rater submission not found"
          messageId="quote.notFound"
        />
      );
    }

    if (!userQuotes) {
      return (
        <ErrorPageLayout
          message="No associated quotes found"
          messageId="quote.notFound"
        />
      );
    }

    return (
      <RaterInnerProvider
        mode={mode}
        policy={policy}
        isEditable={
          isEditable ??
          (allowCreatingQuotes &&
            !underwritingQuote?.isObsolete &&
            !underwritingQuote?.inForcePolicy)
        }
        quote={underwritingQuote}
        submission={submission as Submission}
        userQuotes={userQuotes}
      />
    );
  },
  (prevProps, nextProps) => {
    return prevProps.mode === nextProps.mode;
  }
);

Rater.displayName = 'Rater';

export default Rater;
